import { useAreas, useProjects, useServices } from "@hq/client-react";
import type { Project } from "@hq/contracts";
import { Button, Dialog, Input, toast } from "@z/ui";
import { useId, useRef, useState } from "react";

/** Areas are lightweight project labels, saved through the normal offline/sync write path. */
export function ProjectAreaDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const services = useServices();
  const { data: projects = [] } = useProjects();
  const { data: savedAreas = [] } = useAreas();
  const [area, setArea] = useState(project.area ?? "");
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const inputId = useId();
  const areas = [
    ...new Set([
      ...savedAreas.map((a) => a.name),
      ...projects.flatMap((p) => (p.area ? [p.area] : [])),
    ]),
  ].sort((a, b) => a.localeCompare(b));
  const save = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      const name = area.trim();
      const existing = areas.find((a) => a.toLocaleLowerCase() === name.toLocaleLowerCase());
      await services.projects.setArea(project.id, existing ?? (name || null));
      onClose();
    } catch {
      toast({ title: "Could not change area", variant: "danger" });
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !inFlight.current) onClose();
      }}
    >
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>Move to area</Dialog.Title>
          <Dialog.Description>
            Organize {project.name} under an area, or leave it on its own.
          </Dialog.Description>
        </Dialog.Header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
          className="flex flex-col gap-4"
        >
          <label htmlFor={inputId} className="flex flex-col gap-2 text-sm font-medium">
            Area
            <Input
              id={inputId}
              autoFocus
              value={area}
              placeholder="e.g. Work, Family, Hobbies"
              onChange={(event) => setArea(event.target.value)}
              disabled={saving}
            />
          </label>
          <div className="flex flex-wrap gap-1.5" aria-label="Choose an area">
            {["", ...areas].map((name) => (
              <Button
                key={name}
                type="button"
                size="sm"
                variant={area === name ? "secondary" : "ghost"}
                aria-pressed={area === name}
                disabled={saving}
                onClick={() => setArea(name)}
              >
                {name || "No area"}
              </Button>
            ))}
          </div>
          <Dialog.Footer>
            <Button type="button" variant="ghost" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
